/**
 * Created by Ning Jiang on 11/23/2016.
 */

import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {IStackedReelSymbolsConfig} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Ease, Power3} from "gsap";
import {DragonTribeGameAssets} from "./DragonTribeGameAssets";
import {IDragonTribeLayerConfig} from "./stage/IDragonTribeLayerConfig";
import {IDragonTribeSymbolStates} from "./symbols/DTReelSymbol";

export class DragonTribeGameConfig implements IGameConfig {

    public static instance:DragonTribeGameConfig = new DragonTribeGameConfig();

    BROWSER_FPS:number = 30;

    GAME_WIDTH:number = 1280;
    GAME_HEIGHT:number = 720;

    BET_WIN_MODE:BetWinMode = BetWinMode.BET_WAY;
    SPIN_MODE:SpinMode = SpinMode.AVALANCHE;

    DEFAULT_LAYER_SCALE_OPTION:IDeviceDependentConfig<LayerScaleOption> = {
        desktop : {landscape : LayerScaleOption.FIT_WINDOW},
        mobile : {
            landscape : LayerScaleOption.FIT_WINDOW,
            portrait : LayerScaleOption.REELS_FIT_STAGE
        }
    };

    LAYERS:IDragonTribeLayerConfig = {
        BACKGROUND : {
            name : "background",
            scaleOption : LayerScaleOption.FIT_WINDOW,
            customOptions : {
                frame : {x : 0, y : 0, width : 720, height : 720}
            }
        },
        DRAGON : {name : "dragon"},
        REEL_BACKGROUND : {name : "reelBackground"},
        EXPLOSION : {name : "explosion"},
        SYMBOLS : {name : "symbols"},
        NUDGE : {name : "nudge"},
        REEL_FOREROUND : {name : "reelForeground"},
        REEL_ATTENTION : {name : "reelAttention"},
        BET_WAYS : {name : "betways"},
        WIN_PRESENTATIONS : {name : "winPresentations"},
        FOREGROUND : {name : "foreground"},
        BIG_WIN : {name : "bigWin"},
        WIN_COUNT_UP : {name : "winCountUp"},
        FREESPIN_INTRO : {name : "introScreen", scaleOption : LayerScaleOption.FIT_WINDOW},
        INTRO : {name : "introScreen", scaleOption : LayerScaleOption.FIT_WINDOW},
        TWEAK_MODULE : {name : "tweakModule", scaleOption : LayerScaleOption.FIT_WINDOW}
    };

    REAL_REEL_AREA_HEIGHT:number = 550;
    REEL_AREA_TOP:number = 290;
    REEL_AREA_POS_X:number = 285;
    REEL_AREA_POS_Y:number = 80 - this.REEL_AREA_TOP;
    REEL_AREA_WIDTH:number = 704;
    REEL_AREA_HEIGHT:number = this.REAL_REEL_AREA_HEIGHT + this.REEL_AREA_TOP;
    REEL_AREA_MASK_RECT:PIXI.Rectangle = new PIXI.Rectangle(this.REEL_AREA_POS_X, this.REEL_AREA_POS_Y + this.REEL_AREA_TOP, this.REEL_AREA_WIDTH, this.REAL_REEL_AREA_HEIGHT);


    SYMBOL_STATES:IDragonTribeSymbolStates = {
        normal : {
            prio : 0,
            keyword : "normal",
            texturesCreator : (symName:string, stateKey:string) => {
                return DragonTribeGameAssets.getSymbolStateTextures(symName);
            }
        },
        attention : {
            prio : 2,
            keyword : "attention",
            symbols : ["S", "W", "M"]
        },
        plus : {
            prio : 3,
            keyword : "plus",
            texturesCreator : (symName:string, stateKey:string) => {
                return DragonTribeGameAssets.getSymbolStateTextures(symName, stateKey);
            },
            symbols : ["S"]
        },
        expand : {
            prio : 4,
            keyword : "expand",
            symbols : ["M"],
            fpsGetter : (symName, stateKey) => 15
        },
        spin : {
            prio : 5,
            keyword : "spin",
            texturesCreator : (symName:string, stateKey:string) => {
                return DragonTribeGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        noWin : {
            prio : 6,
            keyword : "noWin",
            texturesCreator : (symName:string, stateKey:string) => {
                return DragonTribeGameAssets.getSymbolStateTextures(symName);
            }
        },
        win : {
            prio : 7,
            keyword : "win",
            texturesCreator : (symName:string, stateKey:string) => {
                return DragonTribeGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        crack : {
            prio : 8,
            keyword : "crack",
            texturesCreator : (symName:string, stateKey:string) => {
                return DragonTribeGameAssets.getSymbolStateTextures(symName);
            }
        },
        hidden : {
            prio : 9,
            keyword : "hidden"
        }
    };

    STACKED_SYMBOLS:IStackedReelSymbolsConfig = {
        "W0" : {
            totalNum : [4, 4, 4, 4, 4, 4],
            reels : [0, 1, 2, 3, 4, 5]
        }
    };

    SYMBOL_WIDTH:number = 114;
    SYMBOL_HEIGHT:number = 280;
    SYMBOL_BOTTOM_PADDING:number = 0;

    REELS_NUM:number = 6;
    SPACE_BETWEEN_REELS:number = 0;
    REEL_DISPLAY_POSITIONS:[number, number][] = [
        [10 + this.SYMBOL_WIDTH * 0.5, 510 + this.REEL_AREA_TOP],
        [10 + this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 510 + this.REEL_AREA_TOP],
        [10 + this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 510 + this.REEL_AREA_TOP],
        [10 + this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 510 + this.REEL_AREA_TOP],
        [10 + this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 510 + this.REEL_AREA_TOP],
        [10 + this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, 510 + this.REEL_AREA_TOP]];
    SYMBOLS_NUM_IN_REEL:[number, number, number, number, number] = [0, 0, 6, 0, 0];

    REEL_SPIN_TIME:number = 1;
    REEL_FAST_SPIN_SPIN_TIME:number = 0.8;
    MIN_STOPPING_TIME:number = 1;

    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION:number = 0.3;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY:number = 0.3;
    REEL_SPIN_START_EASE:Ease = Power3.easeIn;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION:number = 0.2;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY:number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG:[number, number] = [1, 1];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG:[number, number] = [1, 1];

    REEL_SPIN_START_SPEED:number = 0.5;
    REEL_FAST_SPIN_START_SPEED:number = 0.4;
    REEL_SPIN_SPIN_SPEED:number = 2;
    REEL_NEAR_WIN_SPIN_SPEED:number = 0.6;
    REEL_SPIN_STOP_SPEED:number = 0.25;
    REEL_SPIN_QUICK_STOP_SPEED:number = 0.23;

    REEL_START_DELAY:number = 0.1;
    REEL_FAST_SPIN_START_DELAY:number = 0.08;
    REEL_STOP_DELAY:number = 0.1;
    REEL_QUICK_STOP_DELAY:number = 0.08;
    REEL_NEAR_WIN_STOP_DELAY:number = 0.8;

    REEL_SPIN_START_SYMBOL_DELAY:number = 0.1;
    REEL_FAST_SPIN_START_SYMBOL_DELAY:number = 0.1;
    REEL_SPIN_STOP_SYMBOL_DELAY:number = 0.1;
    REEL_SPIN_QUICK_STOP_SYMBOL_DELAY:number = 0.08;
    REEL_SPIN_NEAR_WIN_STOP_SYMBOL_DELAY:number = 0.8;

    REEL_SETS:IReelSets = {
        "BASE_REELSET" : [
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"]
        ],
        "FREESPIN_REELSET" : [
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"]
        ]
    };

    WIN_RATIOS:IWinRatios = {
        normalWin : [
            {
                ratio : 0,
                text : "Tiny Win",
                duration : 0,
                type : WinRatioType.TINY
            },
            {
                ratio : 1.5,
                text : "Small Win",
                duration : 0,
                type : WinRatioType.SMALL
            },
            {
                ratio : 3,
                text : "Medium Win",
                duration : 1.5,
                type : WinRatioType.MEDIUM
            },
            {
                ratio : 5,
                text : "Large Win",
                duration : 1.6,
                type : WinRatioType.LARGE
            }
        ],
        bigWin : [
            {
                ratio : 15,
                text : "Big Win",
                duration : 4.583,
                type : WinRatioType.BIG_WIN
            },
            {
                ratio : 25,
                text : "Mega Win",
                duration : 4.444,
                type : WinRatioType.MEGA_WIN

            },
            {
                ratio : 50,
                text : "Super Mega Win",
                duration : 4.414,
                type : WinRatioType.SUPER_MEGA_WIN
            }
        ]
    };

    // GAME_TWEAKER_CONFIG:IGameTweakerConfig = {
    //     layer : this.LAYERS.TWEAK_MODULE.name,
    //     baseWindowWidth : 400,
    //     moduleHeight : 32,
    //     copyAllButtonText : "Copy All",
    //     buttonSkinIdle : "button",
    //     buttonSkinOver : "button",
    //     buttonSkinDown : "button",
    //     buttonSkinDisabled : "button-disabled"
    // };
    PERFORMANCE_LOGGER:boolean = true;

    // #315
    WIN_BELOW_STAKE: boolean = false;

    private constructor() {
        if(DragonTribeGameConfig.instance) {
            throw new Error("DragonTribeGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }
}